import { z } from 'zod';
import { OperationCode, type LedgerType } from '../enums';

/**
 * Баланс (§32). Единицы — целые числа, дробей в системе нет.
 *
 * `reserved` — то, что заблокировано под запущенные генерации (§37).
 * Показывать его отдельно обязательно: иначе пользователь не поймёт, почему
 * доступных единиц меньше, чем он ожидал.
 */
export interface WalletView {
  available: number;
  reserved: number;
  total: number;
}

/** Направление операции для истории §75. */
export const ledgerDirection: Record<LedgerType, 'in' | 'out' | 'neutral'> = {
  credit: 'in',
  bonus: 'in',
  release: 'in',
  refund: 'in',
  reserve: 'out',
  debit: 'out',
  adjustment: 'neutral',
};

export const ledgerTypeLabels: Record<LedgerType, string> = {
  credit: 'Пополнение',
  bonus: 'Бонус',
  reserve: 'Резерв',
  debit: 'Списание',
  release: 'Возврат резерва',
  refund: 'Возврат',
  adjustment: 'Корректировка',
};

/** Человеческие названия операций — в истории не должно быть кодов (§52). */
export const operationLabels: Record<string, string> = {
  [OperationCode.AVATAR_CREATE]: 'Создание аватара',
  [OperationCode.VOICE_CLONE]: 'Создание голоса',
  [OperationCode.VOICE_CLONE_UPGRADE]: 'Улучшение голоса',
  [OperationCode.LOOK_GENERATE]: 'Создание образа',
  [OperationCode.IMAGE_GENERATE]: 'Создание изображения',
  [OperationCode.AVATAR_VIDEO_SECOND]: 'Сцена с вами',
  [OperationCode.DYNAMIC_VIDEO_SECOND]: 'Динамичная сцена',
  [OperationCode.BROLL_SECOND]: 'Видео-вставка',
  [OperationCode.MOTION_SECOND]: 'Графика',
  [OperationCode.LIP_SYNC_SECOND]: 'Синхронизация речи',
  [OperationCode.FINAL_RENDER_SECOND]: 'Сборка видео',
  scene_persona_speech: 'Сцена с вами',
  scene_dynamic: 'Динамичная сцена',
  scene_broll: 'Видео-вставка',
  scene_image: 'Изображение',
  topup: 'Пополнение баланса',
  referral_reward: 'Реферальный бонус',
  manual: 'Ручная корректировка',
};

export interface LedgerEntryView {
  id: string;
  type: LedgerType;
  /** Магнитуда в единицах, всегда положительная. Знак задаёт `direction`. */
  amount: number;
  direction: 'in' | 'out' | 'neutral';
  /** Знаковое изменение доступного баланса — то, что реально увидел пользователь. */
  availableDelta: number;
  operation: string | null;
  title: string;
  createdAt: string;
  availableAfter: number;
}

export const ledgerQuerySchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    /**
     * По умолчанию резерв и его возврат скрыты: это внутренняя механика,
     * а не движение денег. Админке и разбору инцидентов нужен полный список.
     */
    includeInternal: z
      .union([z.boolean(), z.enum(['true', 'false'])])
      .default(false)
      .transform((value) => value === true || value === 'true'),
  })
  .strict();
export type LedgerQuery = z.infer<typeof ledgerQuerySchema>;
